import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

/** Test data belonging to one client or partner. */
export interface TestOwnerRow {
  scope: 'CLIENT' | 'PARTNER';
  ownerId: number;
  name: string | null;
  code: string | null;
  members: number;
  payments: number;
}

export interface TestModeStatus {
  enabled: boolean;
  testMembers: number;
  testPayments: number;
  liveMembers: number;
  livePayments: number;
  byOwner: TestOwnerRow[];
}

/**
 * Global test mode.
 *
 * While it is ON, every enrolment is stamped IsTestData and draws its member number from a
 * SEPARATE series (TEST-2026-27-0000001) with its own counter, so test activity never advances
 * the live counter. "Clear test data" then deletes only the stamped rows.
 *
 * The `enabled` signal is shared app-wide so the banner and the admin page always agree; it is
 * loaded once at startup and updated whenever the toggle is used.
 */
@Injectable({ providedIn: 'root' })
export class TestModeService {
  private http = inject(HttpClient);
  private readonly api = `${environment.apiUrl}/TestMode`;

  /** Drives the persistent banner. Starts false so nothing flashes before the first load. */
  readonly enabled = signal(false);
  readonly status = signal<TestModeStatus | null>(null);

  /**
   * WHAT THE USER IS LOOKING AT — not a system setting.
   *
   * Mode itself now belongs to each client/partner, so "the system" is never wholly live or
   * wholly test: one client can be selling for real while another pilots. This flag only decides
   * whether dashboards and lists show live records or test records, and is remembered per browser.
   */
  readonly viewTest = signal(false);

  /**
   * The view the SERVER pinned for this caller, for users who have no switch.
   *
   * A partner works inside exactly one world — the one its own Mode puts it in — so the backend
   * ignores the header and pins the view to that mode. Without this signal the partner would be
   * looking at test records with nothing on screen saying so.
   */
  readonly pinnedTest = signal(false);
  readonly pinnedOwner = signal<string | null>(null);

  /** What the banner should react to: the admin's chosen view, or a partner's pinned one. */
  readonly showingTest = computed(() => this.viewTest() || this.pinnedTest());

  private static readonly VIEW_KEY = 'arogya.viewTest';

  /** Restore the chosen view. Safe to call before login. */
  initView(): void {
    try {
      this.viewTest.set(localStorage.getItem(TestModeService.VIEW_KEY) === '1');
    } catch {
      /* storage unavailable — default to the live view */
    }
  }

  setView(test: boolean): void {
    this.viewTest.set(test);
    try {
      localStorage.setItem(TestModeService.VIEW_KEY, test ? '1' : '0');
    } catch {
      /* storage unavailable — the choice just won't survive a reload */
    }
  }

  /** Admin-only endpoint: a partner gets 403, which is fine — the banner just stays hidden. */
  refresh() {
    this.http.get<any>(this.api).subscribe({
      next: (res) => {
        const d = res?.data as TestModeStatus | undefined;
        if (!d) return;
        this.status.set(d);
        this.enabled.set(!!d.enabled);
      },
      error: () => {
        /* not an admin, or offline — leave the banner off */
      },
    });
  }

  set(enabled: boolean) {
    return this.http.put<any>(this.api, { enabled });
  }

  /**
   * Ask the server which world THIS caller is in. Open to every signed-in user (unlike `refresh`,
   * which is admin-only), because a partner needs the answer most: it has no switch to consult.
   */
  refreshPinned(): void {
    this.http.get<any>(`${environment.apiUrl}/SystemStatus`).subscribe({
      next: (res) => {
        const d = res?.data;
        if (!d) return;
        this.pinnedTest.set(!!d.testMode);
        this.pinnedOwner.set(d.ownerName ?? null);
      },
      error: () => {
        /* offline — leave the banner off rather than claiming a world we could not confirm */
      },
    });
  }

  /** Omit the scope to clear all test data; pass one id to clear just that owner's. */
  reset(scope?: { clientId?: number; partnerId?: number }) {
    return this.http.post<any>(`${this.api}/Reset`, scope ?? {});
  }
}
